import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Grid, InputBase, Link, Stack, Typography, styled } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import firebaseUtil from '../../../../utils/firebaseUtil';
import palette from '../../../../theme/palette';
import useCountdown from '../../../../hooks/useCountdown';
import useLocales from '../../../../hooks/useLocales';
import { getOtpErrorMessage } from '../../../../utils/formatString';
import { checkIfNumberExist } from '../../../../utils/formatObject';

const OTPContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: 'rgb(90, 24, 24)',
  borderRadius: '10px',
  padding: '10px',
  //   marginRight: '1rem',
  //   marginLeft: '1rem',
  marginTop: '1rem',
}));

const InputContainer = styled(Stack)(({ theme }) => ({
  opacity: 0.9,
  alignItems: 'center',
  borderRadius: '5px',
  height: '44px',
  padding: '0 5px',
  backgroundColor: '#fff',
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  opacity: 0.9,
  marginLeft: 8,
  fontSize: '15px',
  fontWeight: 400,
  fontFamily: 'sans-serif',
  flexGrow: 1,
  color: '#000',
  backgroundColor: '#fff',
  input: {
    '&::placeholder': {
      opacity: 1,
      color: palette.visitor.input.placeholder,
    },
  },
}));

const RegisterOTPForm = ({ phoneNumber = '', setVerification = () => {}, verification = {}, isForgot = false }) => {
  const { translate } = useLocales();

  const [otpError, setOTPError] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const verificationExpiry = useRef(null);

  const [resendExpiry, setResendExpiry] = useState(null);

  const countdown = useCountdown(verificationExpiry.current);

  const resendCountdown = useCountdown(resendExpiry);

  const isInit = useMemo(() => firebaseUtil.getIsInitialized(), []);

  useEffect(() => {
    firebaseUtil.initVerifier();
  }, []);

  const sendOtp = async () => {
    try {
      setIsLoading(true);

      if (!phoneNumber) {
        throw new Error('Please input phone number above');
      } else {
        const isExists = await checkIfNumberExist(phoneNumber);

        if (isExists && !isForgot) throw new Error(translate('number_exists'));
        if (!isExists && isForgot) throw new Error(translate('number_not_exists'));

        const verificationId = await firebaseUtil.handleSendOTP(phoneNumber);
        setVerification((prev) => ({ ...prev, id: verificationId }));
        verificationExpiry.current = moment().add(10, 'minutes').toISOString();
      }
    } catch (error) {
      console.error(error);
      setOTPError(getOtpErrorMessage(translate, error));
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    try {
      setIsLoading(true);

      firebaseUtil.resetVerifier();

      const newVerificationId = await firebaseUtil.handleSendOTP(phoneNumber);
      verificationExpiry.current = moment().add(10, 'minutes').toISOString();
      setResendExpiry(moment().add(1, 'minute').toISOString());
      setVerification((prev) => ({ ...prev, id: newVerificationId }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onClickButton = () => {
    setOTPError('');
    sendOtp();
  };

  return (
    <OTPContainer spacing={1}>
      {!isInit && <Alert color="warning">{translate('otp_not_found')}</Alert>}
      {isInit ? (
        <>
          <Typography fontSize={'15px'}>
            {verification?.id ? translate('otp_sent') : translate('press_get_code')}
          </Typography>
          {otpError && <Alert color="error">{otpError}</Alert>}

          <Box>
            <Grid container spacing={1}>
              <Grid item xs={8}>
                <InputContainer direction="row">
                  <StyledInput
                    name="otp"
                    placeholder={translate('verification_code')}
                    value={verification?.otp}
                    onChange={(e) => setVerification((prev) => ({ ...prev, otp: e.target.value }))}
                  />
                </InputContainer>
              </Grid>
              <Grid item xs={4}>
                <LoadingButton
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: 'warning.dark',
                    boxShadow: 'none',
                    height: '100%',
                    textTransform: 'uppercase',
                    '&:hover': { backgroundColor: 'warning.dark' },
                    whiteSpace: 'nowrap',
                  }}
                  loading={isLoading}
                  disabled={verification?.id}
                  onClick={onClickButton}
                >
                  {translate('get_code')}
                </LoadingButton>
              </Grid>
            </Grid>

            {verification?.id && !verification?.isVerified ? (
              <>
                <Box>
                  <Typography variant="caption">{translate('your_otp_expire')} </Typography>
                  <Typography variant="caption">
                    {countdown.minutes}:{countdown.seconds}
                  </Typography>
                </Box>

                {!resendExpiry || resendCountdown?.seconds === '00' ? (
                  <Typography
                    variant="body2"
                    sx={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.6 : 1 }}
                  >
                    {translate('didnt_receive')}{' '}
                    <Link
                      color={'warning'}
                      onClick={() => (!isLoading ? resendOtp() : console.log('Already Sent'))}
                      sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      {translate('send_again')}
                    </Link>
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    {translate('resend_again')} {resendCountdown?.seconds}
                  </Typography>
                )}
              </>
            ) : (
              ''
            )}
          </Box>
        </>
      ) : (
        ''
      )}
    </OTPContainer>
  );
};

export default RegisterOTPForm;
